import { createFileRoute, Link } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING, LAST_REVIEWED } from "@/lib/official-sources";
import { EXAM_TOPICS } from "@/lib/exam-topics";

const URL = "https://alkoholprovet.se/provfragor";

const faqs = [
  { q: "Är det här kommunens riktiga provfrågor?", a: "Nej. Kommunens kunskapsprov är sekretessbelagt. Våra frågor är pedagogiska övningsfrågor som bygger på alkohollagen och Folkhälsomyndighetens vägledning, formulerade i samma stil som provet." },
  { q: "Hur många ämnesområden ingår i provet?", a: "Kunskapsprovet täcker bland annat alkoholpolitik, alkohollagen, tillsyn, serveringsansvar, ålderskontroll, marknadsföring och egenkontroll. Frågebanken är indelad i samma områden." },
  { q: "Kostar det något att öva?", a: "Du kan öva gratis varje dag. Hela frågebanken med rätt svar, förklaringar och provsimulering ingår i Premium." },
  { q: "Hur många frågor bör jag klara innan provet?", a: "Vår rekommendation är att du klarar minst 80 procent rätt i två fullständiga provsimuleringar i rad innan du bokar provet hos kommunen." },
];

export const Route = createFileRoute("/provfragor/")({
  head: () => ({
    meta: [
      { title: "Provfrågor per ämnesområde – kunskapsprovet serveringstillstånd" },
      { name: "description", content: "Öva på provfrågor uppdelat per ämnesområde: tillsyn, alkoholpolitik, serveringsansvar, ålderskontroll, marknadsföring och egenkontroll." },
      { property: "og:title", content: "Provfrågor per ämnesområde" },
      { property: "og:description", content: "Öva inför kunskapsprovet ämne för ämne – med exempelfrågor ur vår frågebank." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Provfrågor per ämnesområde" },
      { name: "twitter:description", content: "Öva inför kunskapsprovet ämne för ämne." },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Provfrågor", url: URL },
      ]),
    ],
  }),
  component: TopicsHub,
});

function TopicsHub() {
  return (
    <SeoPage
      breadcrumbs={[{ label: "Hem", to: "/" }, { label: "Provfrågor" }]}
      title="Provfrågor per ämnesområde"
      intro="Kunskapsprovet för serveringstillstånd är indelat i ämnesområden. Välj ett område nedan och öva på frågor i samma stil som provet – med genomgång av reglerna, exempelfrågor och vanliga frågor."
      lastReviewed={LAST_REVIEWED}
      sources={[SOURCE_ALKOHOLLAGEN, SOURCE_FOHM_VAGLEDNING]}
      offerBlock={
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold">Välj ämnesområde</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {EXAM_TOPICS.map((t) => (
              <Link
                key={t.slug}
                to="/provfragor/$topic"
                params={{ topic: t.slug }}
                className="rounded-xl border border-border/70 bg-background p-4 hover:border-accent/50"
              >
                <div className="font-medium">{t.category}</div>
                <p className="mt-1 text-sm text-muted-foreground">{t.metaDescription}</p>
              </Link>
            ))}
          </div>
        </section>
      }
      sections={[
        { h2: "Så är kunskapsprovet upplagt", body: "Provet görs hos kommunen via Folkhälsomyndighetens provsystem och innehåller frågor från flera ämnesområden: alkoholpolitik, alkohollagen och bestämmelser om servering, tillsyn samt praktisk drift som ålderskontroll och ordning. Du behöver klara en viss andel rätt inom varje delområde – det räcker alltså inte att vara stark på ett område." },
        { h2: "Öva ämne för ämne först, prov sedan", body: "Den mest effektiva metoden är att först arbeta igenom ett ämnesområde i taget till du känner att reglerna sitter, och först därefter köra hela provsimuleringar på tid. Då upptäcker du vilka områden som drar ner resultatet innan du sitter på provet." },
        { h2: "Vad frågorna bygger på", body: "Frågorna bygger på alkohollagen (2010:1622), Folkhälsomyndighetens vägledning och kommunernas riktlinjer. Förklaringen till varje fråga hänvisar till det kapitel eller den paragraf som svaret följer av, så att du förstår logiken i stället för att memorera." },
        { h2: "Frågor på flera språk", body: "Frågebanken finns översatt till flera språk, bland annat arabiska, engelska, persiska och turkiska. Du byter språk direkt i appen och behåller samma frågor och förklaringar." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          ...EXAM_TOPICS.map((t) => ({ to: `/provfragor/${t.slug}`, label: `Provfrågor ${t.category.toLowerCase()}` })),
          { to: "/gratis-ovningsfragor-serveringstillstand", label: "Gratis övningsfrågor" },
          { to: "/ovningsprov-serveringstillstand", label: "Övningsprov på tid" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
        ],
      }}
    />
  );
}
